import { z } from "zod";

import { type FileMessageContent } from "./Chat";
import { MediaLibraryResourceType, VideoUploadStatus, type MediaLibraryVideo } from "./MediaLibrary";

/** Storage folder (bucket `chat`) for attachments of a message sent to many clients at once (TRE-2306). */
export const CHAT_BULK_ATTACHMENTS_FOLDER = "bulk";

export const MAX_BULK_MESSAGE_ATTACHMENTS = 10;

/** Executables never go out to clients; the admin uploader's own default list is `exe`, `bin`. */
export const EXECUTABLE_ATTACHMENT_EXTENSIONS = [
  "exe",
  "bin",
  "bat",
  "cmd",
  "com",
  "msi",
  "scr",
  "sh",
  "jar",
  "apk",
  "dmg",
];

/** Videos for a bulk send come from the media library, already encoded; uploads would be transcoded per recipient. */
export const VIDEO_ATTACHMENT_EXTENSIONS = [
  "mp4",
  "mov",
  "m4v",
  "avi",
  "mkv",
  "webm",
  "hevc",
  "3gp",
  "wmv",
  "mpg",
  "mpeg",
];

/**
 * A sent chat attachment as the clients receive it. Upload-progress fields are deliberately
 * absent; GIFs and voice notes never come from a bulk send, so they are not accepted here.
 */
export const fileMessageContentSchema = z.object({
  uid: z.string().min(1),
  originalName: z.string(),
  shortName: z.string(),
  size: z.number().nonnegative(),
  contentType: z.string(),
  extension: z.string(),
  url: z.string().url(),
  thumbUrl: z.string().url().nullish(),
  duration: z.number().nullish(),
}) satisfies z.ZodType<FileMessageContent>;

/** An uploaded attachment as forms hold it, before it is sent. */
export type UploadedMessageAttachment = z.infer<typeof fileMessageContentSchema>;

export const isVideoAttachment = (file: Pick<FileMessageContent, "contentType">) =>
  file.contentType.startsWith("video");

export const isExecutableAttachment = (file: Pick<FileMessageContent, "extension">) =>
  EXECUTABLE_ATTACHMENT_EXTENSIONS.includes(file.extension.toLowerCase());

/**
 * A media library video sent as a chat attachment, the way campaigns embed it. Bunny CDN URLs
 * are signed for ten years, so the message can carry them as-is. `videoProcessingStatus`
 * is "ready" because Bunny already encoded the file - chat-service must not transcode it again.
 * Null when the video is not a marketing one or has no playable MP4 yet.
 */
export const mediaLibraryVideoToAttachment = (video: MediaLibraryVideo): FileMessageContent | null => {
  // Only the marketing library the pickers offer: Studio sources and clips, exercise and
  // challenge videos are not meant for client chats, and the lookup runs as the service role.
  if (
    video.resourceType !== MediaLibraryResourceType.Marketing ||
    video.data.uploadStatus !== VideoUploadStatus.Ready ||
    !video.data.mp4Url
  ) {
    return null;
  }

  return {
    uid: video.id,
    originalName: `${video.title}.mp4`,
    shortName: video.title,
    size: video.data.storageSize ?? 0,
    contentType: "video/mp4",
    extension: "mp4",
    url: video.data.mp4Url,
    thumbUrl: video.data.thumbnailUrl,
    duration: video.data.length ? Math.round(video.data.length * 1000) : null,
    videoProcessingStatus: "ready",
  };
};
